import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { G, FONT_FAMILY, OVERVIEW } from "../constants/theme";
import { useTour } from "./TourContext";

// Layout constants. The pinned-header height differs per screen, so the sticky
// offset comes from the active module config (TOUR_MODULES) at render time.
const VP_MARGIN = 16;
const GAP = 14; // spotlight ↔ callout
const SPOT_PAD = 10; // small, consistent padding around every spotlight
const CALLOUT_W = 360;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

function waitFor(selector, onFound, onFail) {
  let tries = 0;
  const tick = () => {
    const el = document.querySelector(selector);
    if (el) return onFound(el);
    if (++tries > 20) return onFail();
    setTimeout(tick, 100);
  };
  tick();
}

function afterScrollSettled(cb) {
  let last = -1;
  let stable = 0;
  let frames = 0;
  const tick = () => {
    frames += 1;
    const y = window.scrollY;
    if (Math.abs(y - last) < 1) stable += 1;
    else stable = 0;
    last = y;
    if (stable >= 3 || frames > 90) return cb();
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

export default function TourOverlay() {
  const { active, moduleConfig, stepIndex, stepCount, next, back, close, goToModule } = useTour();
  const navigate = useNavigate();
  const step = active ? moduleConfig.steps[stepIndex] : null;

  const stickyOffset = moduleConfig.stickyOffset;
  const scrollOffset = stickyOffset + 20; // where the target's top should land

  // Last step of a module → hand off to the next module, or finish the tour.
  const finish = useCallback(() => {
    const h = moduleConfig.handoff;
    if (h) {
      navigate(h.route);
      goToModule(h.module);
    } else {
      close();
    }
  }, [moduleConfig, navigate, goToModule, close]);

  const [spot, setSpot] = useState(null); // { top, left, width, height }
  const [callout, setCallout] = useState(null); // { top, left, width, placement, beak }
  const [shown, setShown] = useState(false);

  const calloutRef = useRef(null);
  const nextBtnRef = useRef(null);
  const reducedRef = useRef(false);

  // ── measure target + compute spotlight & callout geometry ──────────────────
  const place = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.target);
    if (!el) return;

    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Spotlight = the target element's FULL bounding rect + a small, uniform
    // padding. No width clamping, no height capping — the whole section is lit,
    // edge to edge, and the dim overlay covers everything outside it.
    const s = {
      top: r.top - SPOT_PAD,
      left: r.left - SPOT_PAD,
      width: r.width + SPOT_PAD * 2,
      height: r.height + SPOT_PAD * 2,
    };

    // Callout geometry
    const cw = Math.min(CALLOUT_W, vw - VP_MARGIN * 2);
    const ch = calloutRef.current ? calloutRef.current.offsetHeight : 210;

    const centerX = clamp(s.left + s.width / 2, VP_MARGIN, vw - VP_MARGIN);
    let cLeft = clamp(centerX - cw / 2, VP_MARGIN, vw - cw - VP_MARGIN);

    const belowY = s.top + s.height + GAP;
    const aboveY = s.top - GAP - ch;
    const fitsBelow = belowY + ch <= vh - VP_MARGIN;
    const fitsAbove = aboveY >= stickyOffset + VP_MARGIN;
    const preferTop = step.placement === "top";

    let cTop;
    let placement;
    if (preferTop && fitsAbove) {
      cTop = aboveY;
      placement = "above";
    } else if (fitsBelow) {
      cTop = belowY;
      placement = "below";
    } else if (fitsAbove) {
      cTop = aboveY;
      placement = "above";
    } else {
      // Section is taller than the room around it → float the callout over the
      // lit section near the bottom of the viewport; the arrow points up into it.
      const visTop = Math.max(s.top, stickyOffset + VP_MARGIN);
      cTop = clamp(vh - ch - VP_MARGIN, visTop + 8, vh - ch - VP_MARGIN);
      placement = "overlay";
    }

    const beak = {
      edge: placement === "above" ? "bottom" : "top",
      offset: clamp(centerX - cLeft - 7, 14, cw - 28),
    };

    setSpot(s);
    setCallout({ top: cTop, left: cLeft, width: cw, placement, beak });
  }, [step, stickyOffset]);

  // ── advance to a step: scroll → settle → place → reveal ────────────────────
  useEffect(() => {
    if (!active || !step) {
      setSpot(null);
      setCallout(null);
      setShown(false);
      return;
    }

    reducedRef.current =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    setShown(false);
    let cancelled = false;
    let ro = null;

    waitFor(
      step.target,
      (el) => {
        if (cancelled) return;
        const y = Math.max(0, window.scrollY + el.getBoundingClientRect().top - scrollOffset);
        window.scrollTo({ top: y, behavior: reducedRef.current ? "auto" : "smooth" });
        afterScrollSettled(() => {
          if (cancelled) return;
          place(); // first pass (estimated callout height)
          requestAnimationFrame(() => {
            if (cancelled) return;
            place(); // second pass (real callout height)
            setShown(true);
            requestAnimationFrame(() => {
              if (nextBtnRef.current) nextBtnRef.current.focus({ preventScroll: true });
            });
          });
        });
        // Track the target's real box — it can grow/shrink as data loads.
        if (typeof ResizeObserver === "function") {
          ro = new ResizeObserver(() => place());
          ro.observe(el);
        }
      },
      () => {
        if (!cancelled) next(); // target genuinely missing → skip
      }
    );

    return () => {
      cancelled = true;
      if (ro) ro.disconnect();
    };
  }, [active, stepIndex, step, place, next, scrollOffset]);

  // ── keep aligned on resize (and only re-track scroll once settled) ────────
  useEffect(() => {
    if (!active) return;
    let raf = 0;
    const onChange = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(place);
    };
    const onScroll = () => {
      if (shown) onChange();
    };
    window.addEventListener("resize", onChange);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onChange);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [active, shown, place]);

  // ── lock user scrolling while the tour is active ──────────────────────────
  useEffect(() => {
    if (!active) return;
    const prevent = (e) => e.preventDefault();
    const opts = { passive: false, capture: true };
    window.addEventListener("wheel", prevent, opts);
    window.addEventListener("touchmove", prevent, opts);
    return () => {
      window.removeEventListener("wheel", prevent, opts);
      window.removeEventListener("touchmove", prevent, opts);
    };
  }, [active]);

  // ── keyboard ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (stepIndex >= stepCount - 1) finish();
        else next();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        back();
      } else if (["PageUp", "PageDown", "Home", "End", "ArrowUp", "ArrowDown", " "].includes(e.key)) {
        e.preventDefault(); // block page scroll
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [active, next, back, close, finish, stepIndex, stepCount]);

  const trapTab = (e) => {
    if (e.key !== "Tab") return;
    const root = calloutRef.current;
    if (!root) return;
    const f = root.querySelectorAll("button:not([disabled])");
    if (!f.length) return;
    const first = f[0];
    const lastEl = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      lastEl.focus();
    } else if (!e.shiftKey && document.activeElement === lastEl) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!active || !step) return null;

  const reduced = reducedRef.current;
  const isLast = stepIndex === stepCount - 1;
  const lastLabel = moduleConfig.handoff ? moduleConfig.handoff.label : "Done";

  return createPortal(
    <div
      className="jiraops-tour"
      style={{ position: "fixed", inset: 0, zIndex: 9998, pointerEvents: "none" }}
    >
      {/* click / context-menu catcher over the whole page */}
      <div
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onContextMenu={(e) => e.preventDefault()}
        style={{ position: "absolute", inset: 0, pointerEvents: "auto", cursor: "default" }}
      />

      {/* spotlight — the huge box-shadow is the dim; the div itself is the hole */}
      {spot && (
        <div
          style={{
            position: "absolute",
            top: spot.top,
            left: spot.left,
            width: spot.width,
            height: spot.height,
            borderRadius: 12,
            border: "1px solid rgba(147,197,253,0.35)",
            boxShadow: "0 0 0 9999px rgba(7,26,47,0.66)",
            pointerEvents: "none",
            transition: reduced ? "none" : "top 0.32s cubic-bezier(0.4,0,0.2,1), left 0.32s cubic-bezier(0.4,0,0.2,1), width 0.32s cubic-bezier(0.4,0,0.2,1), height 0.32s cubic-bezier(0.4,0,0.2,1)",
          }}
        />
      )}

      {/* callout */}
      {callout && (
        <div
          ref={calloutRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="jiraops-tour-title"
          aria-describedby="jiraops-tour-body"
          onKeyDown={trapTab}
          style={{
            position: "absolute",
            top: callout.top,
            left: callout.left,
            width: callout.width,
            boxSizing: "border-box",
            background: G.darkSlate,
            border: `1px solid ${G.darkBorder}`,
            borderRadius: OVERVIEW.cardRadius,
            boxShadow: "0 24px 56px -12px rgba(0,0,0,0.6)",
            padding: "18px 20px",
            fontFamily: FONT_FAMILY,
            color: "#FFFFFF",
            pointerEvents: "auto",
            opacity: shown ? 1 : 0,
            transform: shown ? "translateY(0)" : "translateY(4px)",
            transition: reduced ? "none" : "opacity 0.2s ease, transform 0.2s ease",
          }}
        >
          {callout.beak && (
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                width: 12,
                height: 12,
                background: G.darkSlate,
                borderTop: callout.beak.edge === "top" ? `1px solid ${G.darkBorder}` : "none",
                borderLeft: callout.beak.edge === "top" ? `1px solid ${G.darkBorder}` : "none",
                borderBottom: callout.beak.edge === "bottom" ? `1px solid ${G.darkBorder}` : "none",
                borderRight: callout.beak.edge === "bottom" ? `1px solid ${G.darkBorder}` : "none",
                [callout.beak.edge === "top" ? "top" : "bottom"]: -7,
                left: callout.beak.offset,
                transform: "rotate(45deg)",
              }}
            />
          )}

          <div style={{ ...OVERVIEW.eyebrow, color: "rgba(255,255,255,0.4)", marginBottom: 10 }}>
            {stepIndex + 1} / {stepCount}
          </div>
          <div
            id="jiraops-tour-title"
            style={{ fontSize: 15, fontWeight: 700, letterSpacing: "-0.01em", marginBottom: 8 }}
          >
            {step.title}
          </div>
          <div
            id="jiraops-tour-body"
            style={{ fontSize: 13, lineHeight: 1.6, color: "rgba(255,255,255,0.72)" }}
          >
            {step.body}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginTop: 16,
            }}
          >
            <button
              type="button"
              onClick={close}
              style={{
                background: "transparent",
                border: "none",
                color: "rgba(255,255,255,0.42)",
                fontSize: 12,
                fontWeight: 500,
                fontFamily: FONT_FAMILY,
                cursor: "pointer",
                padding: "6px 2px",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.75)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.42)")}
            >
              Skip tour
            </button>

            <div style={{ display: "flex", gap: 8 }}>
              {stepIndex > 0 && (
                <button
                  type="button"
                  onClick={back}
                  style={{
                    background: "transparent",
                    border: "1px solid rgba(255,255,255,0.16)",
                    color: "rgba(255,255,255,0.65)",
                    borderRadius: 6,
                    padding: "7px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: FONT_FAMILY,
                    cursor: "pointer",
                    transition: "border-color 0.12s, color 0.12s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)";
                    e.currentTarget.style.color = "#FFFFFF";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.16)";
                    e.currentTarget.style.color = "rgba(255,255,255,0.65)";
                  }}
                >
                  ← Back
                </button>
              )}
              <button
                ref={nextBtnRef}
                type="button"
                onClick={isLast ? finish : next}
                style={{
                  background: G.brand,
                  border: "none",
                  color: "#FFFFFF",
                  borderRadius: 6,
                  padding: "7px 15px",
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: FONT_FAMILY,
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = G.brandHover)}
                onMouseLeave={(e) => (e.currentTarget.style.background = G.brand)}
              >
                {isLast ? lastLabel : "Next →"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
